import { useEffect, useRef, useState } from 'react';

const Pricing = () => {
  const subtitleRef = useRef(null);
  const sectionRef = useRef(null);
  const [showVideoVersion, setShowVideoVersion] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && subtitleRef.current) {
            subtitleRef.current.classList.add('slide-in-from-right');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    const sectionElement = sectionRef.current;
    if (sectionElement) {
      observer.observe(sectionElement);
    }

    return () => {
      if (sectionElement) {
        observer.unobserve(sectionElement);
      }
    };
  }, []);

  // Hotkey listener for 'p' key
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only trigger if not typing in an input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      
      if (e.key === 'p' || e.key === 'P') {
        setShowVideoVersion(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);



  const handleGetQuote = (e) => {
    e.preventDefault();
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      const navbar = document.querySelector('[data-navbar]');
      const navbarHeight = navbar ? navbar.offsetHeight : 140;
      const targetPosition = contactSection.offsetTop - navbarHeight;
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section
      ref={sectionRef}
      id="pricing"
      className="py-12 md:py-16 lg:py-24 px-4 sm:px-6 lg:px-8 m-0 bg-white relative overflow-visible z-[10] w-full max-w-[100vw]"
    >
      <div 
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          backgroundImage: 'url(/images/backgrounds/poly-bottom.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.75,
          transform: 'rotatex(180deg)'
        }}
      ></div>

      <div className="max-w-[1400px] mx-auto w-full relative overflow-visible z-[1] relative">
        {/* Section Title */}
        <h2 className="text-center text-3xl md:text-4xl lg:text-5xl mb-4 md:mb-6 lg:mb-8 text-black relative block w-full font-semibold uppercase tracking-[4px] font-serif border-b-0 pb-4 after:content-[''] after:absolute after:-bottom-[10px] after:left-1/2 after:-translate-x-1/2 after:w-[150px] sm:after:w-[200px] md:after:w-[250px] after:h-[4px] sm:after:h-[5px] md:after:h-[6px] after:bg-[var(--accent-color)]">
          Pricing <span ref={subtitleRef} className="inline-block pricing-subtitle">That Fits Your Needs</span>
        </h2>

        {/* Pricing Intro */}
        <div className="max-w-[900px] mx-auto mb-6 md:mb-8 text-center">
          <p className="text-lg md:text-xl leading-relaxed text-[var(--text-dark)] font-serif">
            Every organization has unique goals, audiences, and requirements. That&apos;s why we take a customized approach to pricing—rather than offering a one-size-fits-all package. Our team works closely with you to design a solution that aligns with your objectives, timeline, and budget.
          </p>
        </div>

        {/* Pricing Content with Video or Quote Card */}
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 items-start lg:items-center mb-4 md:mb-6 relative z-[2]">
          {/* Video or Quote Card - Left Side */}
          <div className="w-full lg:w-[500px] lg:flex-shrink-0 relative">
            {showVideoVersion ? (
              <>
                <div className="relative overflow-hidden rounded-lg" style={{
                  maskImage: 'radial-gradient(ellipse 90% 90% at center, black 70%, transparent 100%)',
                  WebkitMaskImage: 'radial-gradient(ellipse 90% 90% at center, black 70%, transparent 100%)',
                }}>
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full max-w-[600px] h-auto object-cover object-center scale-110"
                    style={{ objectFit: 'cover', objectPosition: 'center' }}
                  >
                    <source src="/videos/loop-reverse.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
                {/* Soft edge overlay - stronger blend on top */}
                <div className="absolute inset-0 pointer-events-none rounded-lg" style={{
                  background: 'radial-gradient(ellipse 85% 75% at 50% 45%, transparent 60%, rgba(250, 250, 250, 0.4) 75%, rgba(250, 250, 250, 0.7) 90%, rgba(250, 250, 250, 0.95) 100%)',
                  mixBlendMode: 'multiply'
                }}></div>
              </>
            ) : (
              <div className="w-full max-w-[600px] bg-[#9a83ad] p-8 md:p-12 rounded-[15px] shadow-[0_4px_12px_rgba(154,131,173,0.2),0_8px_24px_rgba(154,131,173,0.15)] flex flex-col items-center justify-center gap-5 text-center">
                <h3 className="text-white text-2xl md:text-3xl lg:text-4xl font-semibold m-0 font-serif uppercase tracking-[2px]">
                  Get Your Quote Today
                </h3>
                <button
                  onClick={handleGetQuote}
                  className="inline-block py-3.5 px-9 bg-black text-white no-underline text-[0.95rem] font-semibold uppercase tracking-[1.5px] rounded cursor-pointer transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] shadow-none font-serif border-2 border-black relative overflow-hidden hover:-translate-y-0.5 hover:shadow-lg hover:text-white before:content-[''] before:absolute before:top-0 before:-left-full before:w-full before:h-full before:bg-[#9a83ad] before:transition-[left] before:duration-[500ms] before:ease-[cubic-bezier(0.4,0,0.2,1)] before:-z-10 hover:before:left-0 active:translate-y-0"
                >
                  Contact Us
                </button>
              </div>
            )}
          </div>

          {/* Main Pricing Summary Card */}
          <div className="flex-1 min-w-0 bg-[var(--bg-dark)] p-8 md:p-12 rounded-xl shadow-md border border-[var(--border-dark)] relative z-[1]">
            <p className="text-[var(--bg-pure-white)] leading-relaxed text-base md:text-lg mb-6 font-sans">
            Every organization has unique goals, audiences, and requirements. That&apos;s why we take a customized approach to pricing—rather than offering a one-size-fits-all package. Our team works closely with you to design a solution that aligns with your objectives, timeline, and budget.
          </p>
          <p className="text-[var(--bg-pure-white)] leading-relaxed text-base md:text-lg mb-6 font-sans">
            We work closely with agencies, consultants, and resellers, we do not publish standard pricing. Each engagement is priced individually based on factors such as campaign scope, technical requirements, and messaging volume. This approach allows us to deliver the right solution—without unnecessary costs or compromises.
          </p>
          <p className="text-[var(--bg-pure-white)] leading-relaxed text-base md:text-lg mb-0 font-sans">
            Thank you for considering <strong className="text-[var(--accent-color)] font-semibold">The VBC Group</strong>. We look forward to partnering with you to achieve your goals and deliver <strong className="text-[var(--accent-color)] font-semibold">results you can trust</strong>.
          </p>
          </div>
        </div>

        {/* Get Your Quote Today Card - Only show when video version is active */}
        {showVideoVersion && (
          <div className="w-full max-w-[850px] mx-auto mt-6 md:mt-8 lg:mt-10 bg-[#9a83ad] p-8 md:p-12 rounded-[15px] shadow-[0_4px_12px_rgba(154,131,173,0.2),0_8px_24px_rgba(154,131,173,0.15)] flex flex-col items-center justify-center gap-5 text-center relative z-[1]">
            <h3 className="text-white text-2xl md:text-3xl lg:text-4xl font-semibold m-0 font-serif uppercase tracking-[2px]">
              Get Your Quote Today
            </h3>
            <button
              onClick={handleGetQuote}
              className="inline-block py-3.5 px-9 bg-black text-white no-underline text-[0.95rem] font-semibold uppercase tracking-[1.5px] rounded cursor-pointer transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] shadow-none font-serif border-2 border-black relative overflow-hidden hover:-translate-y-0.5 hover:shadow-lg hover:text-white before:content-[''] before:absolute before:top-0 before:-left-full before:w-full before:h-full before:bg-[#9a83ad] before:transition-[left] before:duration-[500ms] before:ease-[cubic-bezier(0.4,0,0.2,1)] before:-z-10 hover:before:left-0 active:translate-y-0"
            >
              Contact Us
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Pricing;
